// Máscaras e validações dos campos que o hub escreve.
//
// Arquivo puro, sem UM import, e com prova própria (`app/tests/mascaras.mjs`). A
// razão de existir é o que o formulário público aceitava antes: um CPF de 16
// dígitos ("1234567899999999") passava pela tela e só era recusado no banco, e o
// campo de valor em reais aceitava letras — o cliente digitava "R$ 5.000" e o
// banco, que exige `^[0-9]+([.,][0-9]{1,2})?$`, gravava NULO. O aluno via o campo
// vazio e não tinha como saber que o cliente havia respondido.
//
// ---------------------------------------------------------------------------
// A regra que vale para todas: a máscara é APRESENTAÇÃO
// ---------------------------------------------------------------------------
// O que vai para o banco é o valor limpo. `mascararMoeda` devolve as duas coisas
// separadas — o texto que o campo mostra e o número que a RPC recebe —, porque
// mandar "R$ 1.234,56" para uma coluna `numeric` é o mesmo defeito por outro
// caminho: o ponto de milhar quebra o `::numeric`.
//
// A validação continua no banco também, e não é redundância: o dado pode chegar
// por outro caminho (uma carga, a mão de alguém no SQL editor), e validar só na
// tela é validar num lugar que o payload pode não atravessar. Aqui é para o
// cliente do aluno saber ANTES de enviar.

/** Só os dígitos, que é o que toda máscara consome. */
export function soDigitos(valor: string): string {
  return (valor ?? "").replace(/\D/g, "");
}

// ---------------------------------------------------------------------------
// Documentos e contatos
// ---------------------------------------------------------------------------
/** `000.000.000-00`, cortando no 11º dígito. */
export function mascararCpf(valor: string): string {
  const d = soDigitos(valor).slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

/** `00.000.000/0000-00`, cortando no 14º dígito. */
export function mascararCnpj(valor: string): string {
  const d = soDigitos(valor).slice(0, 14);
  if (d.length <= 2) return d;
  if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`;
  if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
  if (d.length <= 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

/**
 * `(00) 00000-0000` para celular e `(00) 0000-0000` para fixo.
 *
 * O corte do grupo do meio depende do TOTAL, e é por isso que o fixo não sai
 * torto: com 10 dígitos o quinto vai para o segundo grupo. Os 3.846 telefones
 * distintos do Bubble têm as duas formas.
 */
export function mascararTelefone(valor: string): string {
  const d = soDigitos(valor).slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

/** `00000-000`, cortando no 8º dígito. */
export function mascararCep(valor: string): string {
  const d = soDigitos(valor).slice(0, 8);
  return d.length <= 5 ? d : `${d.slice(0, 5)}-${d.slice(5)}`;
}

/** UF: duas letras maiúsculas, e nada mais. */
export function mascararUf(valor: string): string {
  return (valor ?? "").replace(/[^A-Za-z]/g, "").slice(0, 2).toUpperCase();
}

/** Inteiro puro — o "número de dependentes". Dois dígitos bastam: o Bubble vai até 11. */
export function mascararInteiro(valor: string, maximo = 2): string {
  return soDigitos(valor).slice(0, maximo).replace(/^0+(?=\d)/, "");
}

// ---------------------------------------------------------------------------
// Dinheiro
// ---------------------------------------------------------------------------
export type Moeda = {
  /** O que o campo mostra: `R$ 1.234,56`. */
  texto: string;
  /** O que a RPC recebe: `1234.56`, ou `null` quando o campo está vazio. */
  numero: number | null;
};

/**
 * Máscara de real por CENTAVOS, da direita para a esquerda.
 *
 * O usuário digita `48000` e vê `R$ 480,00`. É o comportamento de aplicativo de
 * banco, e foi escolhido porque é o único que **nunca** produz valor ambíguo:
 * numa máscara "solta", que aceita ponto e vírgula, "1.234" é mil duzentos e
 * trinta e quatro para um brasileiro e um vírgula dois três quatro para o
 * `parseFloat` — e o campo acaba num contrato, ao lado do valor por extenso.
 *
 * Vazio devolve `numero: null`, e não zero: honorário em branco tem de sair como
 * linha para preencher à mão no contrato, e `R$ 0,00` é um valor.
 */
export function mascararMoeda(valor: string): Moeda {
  const d = soDigitos(valor).slice(0, 12); // teto de R$ 9.999.999.999,99
  if (d === "") return { texto: "", numero: null };

  const centavos = Number(d);
  const numero = centavos / 100;
  return {
    texto: numero.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
      minimumFractionDigits: 2,
    }),
    numero,
  };
}

/** O caminho de volta: do número guardado para o texto do campo. */
export function moedaDeNumero(valor: number | null | undefined): string {
  if (valor === null || valor === undefined) return "";
  return mascararMoeda(String(Math.round(valor * 100))).texto;
}

// ---------------------------------------------------------------------------
// Validações — para avisar ANTES de enviar
// ---------------------------------------------------------------------------
/**
 * Os dois dígitos verificadores do CPF, e a recusa das sequências repetidas.
 *
 * Mesmo algoritmo de `cpf_valido()` (000058), de propósito: se os dois
 * discordassem, a tela aceitaria o que o banco recusa — e o cliente perderia o
 * formulário inteiro numa ida ao servidor. O banco continua sendo a autoridade;
 * isto é cortesia.
 */
export function cpfValido(valor: string): boolean {
  const d = soDigitos(valor);
  if (d.length !== 11) return false;
  // `111.111.111-11` passa na aritmética dos dígitos e não é CPF de ninguém.
  if (/^(\d)\1{10}$/.test(d)) return false;

  for (const [ate, posicao] of [
    [9, 9],
    [10, 10],
  ] as Array<[number, number]>) {
    let soma = 0;
    for (let i = 0; i < ate; i++) soma += Number(d[i]) * (ate + 1 - i);
    const resto = (soma * 10) % 11;
    const digito = resto === 10 ? 0 : resto;
    if (digito !== Number(d[posicao])) return false;
  }
  return true;
}

/**
 * E-mail: uma conta, um domínio com ponto, sem espaço.
 *
 * Deliberadamente mais frouxo que a RFC e mais rígido que o `type="email"` do
 * navegador, que aceita `a@b` (sem ponto no domínio) — e `a@b` é erro de digitação
 * em 100% dos casos reais. O que esta função NÃO faz é afirmar que a caixa existe;
 * para isso só serve mandar a mensagem.
 */
export function emailValido(valor: string): boolean {
  const t = (valor ?? "").trim();
  if (t === "") return false;
  if (/\s/.test(t)) return false;
  return /^[^@]+@[^@.]+(\.[^@.]+)+$/.test(t);
}

/** Celular ou fixo brasileiro: 10 ou 11 dígitos, com DDD que existe. */
export function telefoneValido(valor: string): boolean {
  const d = soDigitos(valor);
  if (d.length !== 10 && d.length !== 11) return false;
  // DDD vai de 11 a 99; e celular (11 dígitos) começa com 9 no quinto caractere.
  if (Number(d.slice(0, 2)) < 11) return false;
  return true;
}

/** CEP: oito dígitos. Se existe, quem responde é o ViaCEP. */
export function cepCompleto(valor: string): boolean {
  return soDigitos(valor).length === 8;
}

// ---------------------------------------------------------------------------
// Os campos que o Simulador MEI trouxe (000098)
// ---------------------------------------------------------------------------
/**
 * CNAE no formato `0000-0/00`, cortando no 7º dígito.
 *
 * Sete dígitos, não oito: a subclasse do CNAE 2.3 é `divisão(2) grupo(1)
 * classe(2) - dígito(1) / subclasse(2)`, e a lista de ocupações do hub guarda
 * exatamente isso ("4930-2/02"). Cortar em oito deixaria o campo aceitar um
 * dígito que nenhuma ocupação tem.
 */
export function mascararCnae(valor: string): string {
  const d = soDigitos(valor).slice(0, 7);
  if (d.length <= 4) return d;
  if (d.length <= 5) return `${d.slice(0, 4)}-${d.slice(4)}`;
  return `${d.slice(0, 4)}-${d.slice(4, 5)}/${d.slice(5)}`;
}

/** Competência do DAS no formato `MM/AAAA`. */
export function mascararCompetencia(valor: string): string {
  const d = soDigitos(valor).slice(0, 6);
  if (d.length <= 2) return d;
  return `${d.slice(0, 2)}/${d.slice(2)}`;
}

/** Data no formato `DD/MM/AAAA`. */
export function mascararData(valor: string): string {
  const d = soDigitos(valor).slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}

/**
 * Os dois dígitos verificadores de um CNPJ, a partir dos 12 primeiros.
 *
 * Separada de `cnpjValido` de propósito: o Simulador MEI precisa GERAR um CNPJ
 * que passe na conferência, e `gerarCnpjFicticio` sorteia os 12 e chama isto.
 * Se a geração tivesse a sua própria cópia da aritmética, o simulador poderia
 * emitir um CCMEI com CNPJ que a própria tela recusa no campo do DAS — que é o
 * caminho real do aluno: ele abre o MEI e depois apura o DAS daquele CNPJ.
 *
 * Devolve `null` se não vierem exatamente 12 dígitos.
 */
export function digitosVerificadoresCnpj(doze: string): string | null {
  const d = soDigitos(doze);
  if (d.length !== 12) return null;

  const calcular = (base: string): number => {
    // Os pesos vão de 2 a 9, da direita para a esquerda, reiniciando em 2.
    let soma = 0;
    let peso = 2;
    for (let i = base.length - 1; i >= 0; i--) {
      soma += Number(base[i]) * peso;
      peso = peso === 9 ? 2 : peso + 1;
    }
    const resto = soma % 11;
    return resto < 2 ? 0 : 11 - resto;
  };

  const primeiro = calcular(d);
  const segundo = calcular(`${d}${primeiro}`);
  return `${primeiro}${segundo}`;
}

/**
 * CNPJ: catorze dígitos e os dois verificadores conferindo.
 *
 * Mesma razão de `cpfValido` existir na tela: o aluno digita o CNPJ na apuração
 * do DAS e na DASN, e descobrir o erro de digitação depois de preencher doze
 * períodos é perder o preenchimento.
 */
export function cnpjValido(valor: string): boolean {
  const d = soDigitos(valor);
  if (d.length !== 14) return false;
  // `00.000.000/0000-00` passa na aritmética e não é CNPJ de ninguém.
  if (/^(\d)\1{13}$/.test(d)) return false;
  return digitosVerificadoresCnpj(d.slice(0, 12)) === d.slice(12);
}
